import { ContentLabel, ContentLabelAction } from 'components/ContentLabel'
import * as React from 'react'
import { observer } from 'mobx-react'
import ProfileViewModel from '../ProfileViewModel'
import ProfileHeader from '../ProfileHeader'
import { ScrollOffsetInterpolation } from 'hooks/spring-hooks'
import { useActivation } from 'hooks/mobx-hooks'
import { useDocumentTitle } from 'hooks/document-title'
import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from 'components/ContentLayout'
import UserInfo from './UserInfo'
import { FormSection } from 'components/inputs'
import Skills from './Skills'
import styles from './General.sass'
import Roles from './Roles'
import ContentLoading from 'components/ContentLoading'
import ErrorBlock from 'components/ErrorBlock'

interface GeneralProps {
  vm: ProfileViewModel
  scrollOffset: ScrollOffsetInterpolation
}

export default observer(function General({ vm, scrollOffset }: GeneralProps) {
  useActivation(vm.general)
  useDocumentTitle('General')
  return (
    <>
      <ProfileHeader viewTitle="General" vm={vm} scrollOffset={scrollOffset} />
      <ContentLayout accountForLabel>
        <ContentMain>
          {vm.general.fetchMemberInfo.match({
            pending: () => <ContentLoading />,
            rejected: (err) => <ErrorBlock error={err} />,
            resolved: () => (
              <>
                <FormSection label="General">
                  <UserInfo vm={vm} />
                </FormSection>
                <div className={styles.columns}>
                  {vm.canViewAndEditOwnSkills && (
                    <div className={styles.column}>
                      <FormSection
                        edgeToEdge
                        label={
                          <SectionLabelWithEdit
                            editTitle="Manage"
                            onEditClick={() =>
                              vm.general.skillSelection.showingEditor.enable()
                            }
                          >
                            Skills
                          </SectionLabelWithEdit>
                        }
                      >
                        <Skills vm={vm} />
                      </FormSection>
                    </div>
                  )}
                  {vm.canViewAndEditOwnRoles && (
                    <div className={styles.column}>
                      <FormSection
                        edgeToEdge
                        label={
                          <SectionLabelWithEdit
                            editTitle="Manage"
                            onEditClick={() =>
                              vm.general.roleSelection.showingEditor.enable()
                            }
                          >
                            Roles
                          </SectionLabelWithEdit>
                        }
                      >
                        <Roles vm={vm} />
                      </FormSection>
                    </div>
                  )}
                </div>
              </>
            ),
          })}
        </ContentMain>
        <ContentTip
          title="Profile and visibility"
          illustrationUrl={require('assets/images/img_workspace-settings.svg')}
        >
          Manage your personal information, and control which information other
          people see and apps may access.
        </ContentTip>
      </ContentLayout>
    </>
  )
})

interface SectionLabelWithEditProps {
  onEditClick?(): unknown
  editTitle: string
  children: React.ReactNode
}

const SectionLabelWithEdit = ({
  onEditClick,
  editTitle,
  children,
}: SectionLabelWithEditProps) => {
  return (
    <ContentLabel
      block
      actions={
        onEditClick && (
          <ContentLabelAction onClick={onEditClick}>
            {editTitle}
          </ContentLabelAction>
        )
      }
    >
      {children}
    </ContentLabel>
  )
}
